import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, ArrowRight, Shuffle, Play, CheckCircle2, Code2, Trophy, Target, AlertTriangle } from "lucide-react";
import { CodeEditor } from "./CodeEditor";
import { ChallengeOutput } from "./ChallengeOutput";
import { useChallenges } from "@/hooks/useChallenges";
import { ChallengesHeader } from "./ChallengesHeader";

type Category = "power_bi" | "sql" | "python";
type Difficulty = "easy" | "medium" | "hard";

const categoryLabels: Record<Category, string> = {
  power_bi: "Power BI (DAX)",
  sql: "SQL",
  python: "Python",
};

const difficultyLabels: Record<Difficulty, string> = {
  easy: "Fácil",
  medium: "Médio",
  hard: "Difícil",
};

const difficultyColors: Record<Difficulty, string> = {
  easy: "bg-emerald-500/20 text-emerald-600 border-emerald-500/30",
  medium: "bg-amber-500/20 text-amber-600 border-amber-500/30",
  hard: "bg-red-500/20 text-red-500 border-red-500/30",
};

export function ChallengesView() {
  const [filterCategory, setFilterCategory] = useState<Category | "all">("all");
  const [filterDifficulty, setFilterDifficulty] = useState<Difficulty | "all">("all");
  const [currentIndex, setCurrentIndex] = useState(0);
  const [userCode, setUserCode] = useState("");
  const [evaluating, setEvaluating] = useState(false);
  const [result, setResult] = useState<{ isCorrect: boolean; feedback: string; suggestions: string[] } | null>(null);
  const [pasteWarning, setPasteWarning] = useState(false);
  const pasteTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handlePasteDetected = useCallback(() => {
    setPasteWarning(true);
    if (pasteTimerRef.current) clearTimeout(pasteTimerRef.current);
    pasteTimerRef.current = setTimeout(() => setPasteWarning(false), 8000);
  }, []);

  useEffect(() => {
    return () => { if (pasteTimerRef.current) clearTimeout(pasteTimerRef.current); };
  }, []);

  const { challenges, loading, completedIds, evaluateCode, categoryStats, globalStats } = useChallenges(
    filterCategory === "all" ? undefined : filterCategory,
    filterDifficulty === "all" ? undefined : filterDifficulty
  );

  const challenge = challenges[currentIndex];

  const handleNavigate = (index: number) => {
    setCurrentIndex(index);
    setUserCode(challenges[index]?.starter_code || "");
    setResult(null);
  };

  const handleRandom = () => {
    if (challenges.length <= 1) return;
    let idx: number;
    do {
      idx = Math.floor(Math.random() * challenges.length);
    } while (idx === currentIndex);
    handleNavigate(idx);
  };

  const handleEvaluate = async () => {
    if (!challenge || !userCode.trim()) return;
    setEvaluating(true);
    setResult(null);
    const res = await evaluateCode(challenge.id, userCode);
    if (res) setResult(res);
    setEvaluating(false);
  };

  // Reset index when filters change
  useMemo(() => {
    setCurrentIndex(0);
    setResult(null);
    if (challenges[0]) setUserCode(challenges[0].starter_code || "");
  }, [filterCategory, filterDifficulty, challenges.length]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Hero Header */}
      <ChallengesHeader categoryStats={categoryStats} globalStats={globalStats} />

      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-foreground">Escolha um desafio</h2>
        <div className="flex items-center gap-2">
          <Select value={filterCategory} onValueChange={(v) => setFilterCategory(v as Category | "all")}>
            <SelectTrigger className="w-[140px] h-8 text-xs">
              <SelectValue placeholder="Categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {(Object.keys(categoryLabels) as Category[]).map((k) => (
                <SelectItem key={k} value={k}>{categoryLabels[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterDifficulty} onValueChange={(v) => setFilterDifficulty(v as Difficulty | "all")}>
            <SelectTrigger className="w-[120px] h-8 text-xs">
              <SelectValue placeholder="Dificuldade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {(Object.keys(difficultyLabels) as Difficulty[]).map((k) => (
                <SelectItem key={k} value={k}>{difficultyLabels[k]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {challenges.length === 0 ? (
        <div className="text-center py-16 space-y-3">
          <Code2 className="h-12 w-12 mx-auto text-muted-foreground/40" />
          <p className="text-muted-foreground">Nenhum desafio disponível para os filtros selecionados</p>
        </div>
      ) : (
        <>
          {/* Split layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Left: Problem statement */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant="outline" className="text-[10px]">
                  Desafio {currentIndex + 1} de {challenges.length}
                </Badge>
                <Badge className={`text-[10px] ${difficultyColors[challenge.difficulty]}`}>
                  {difficultyLabels[challenge.difficulty]}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {categoryLabels[challenge.category]}
                </Badge>
                {completedIds.has(challenge.id) && (
                  <Badge className="text-[10px] bg-emerald-500/20 text-emerald-600 border-emerald-500/30">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Concluído
                  </Badge>
                )}
              </div>

              <h2 className="text-xl font-bold text-foreground">{challenge.title}</h2>

              <div className="prose prose-sm dark:prose-invert max-w-none text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">
                {challenge.description}
              </div>
            </div>

            {/* Right: Code editor + output */}
            <div className="space-y-4">
              {pasteWarning && (
                <Alert className="border-amber-500/30 bg-amber-500/10">
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                  <AlertDescription className="text-amber-600 dark:text-amber-400 text-sm">
                    🤖 Detectamos que você colou um bloco de código. Recomendamos que pratique escrevendo sua própria solução para um melhor aprendizado.
                  </AlertDescription>
                </Alert>
              )}

              <CodeEditor
                value={userCode}
                onChange={(v) => { setUserCode(v); setPasteWarning(false); }}
                language={challenge.category}
                disabled={evaluating}
                onPasteDetected={handlePasteDetected}
              />

              <Button
                onClick={handleEvaluate}
                disabled={evaluating || !userCode.trim()}
                className="w-full gap-2"
              >
                <Play className="h-4 w-4" />
                Verificar Solução
              </Button>

              <ChallengeOutput isLoading={evaluating} result={result} />
            </div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={currentIndex === 0}
              onClick={() => handleNavigate(currentIndex - 1)}
              className="gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Anterior
            </Button>
            <Button variant="outline" size="sm" onClick={handleRandom} className="gap-1">
              <Shuffle className="h-3.5 w-3.5" />
              Aleatório
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={currentIndex === challenges.length - 1}
              onClick={() => handleNavigate(currentIndex + 1)}
              className="gap-1"
            >
              Próximo
              <ArrowRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
